import { type ApolloError, useSubscription } from '@apollo/client'
import { useCallback, useMemo, useState } from 'react'

import { useUser } from '@/client/context/user'
import { NOTIFICATIONS_SUBSCRIPTION } from '@/client/services/hasura'
import type {
  NotificationFragment,
  NotificationsSubscription,
  NotificationsSubscriptionVariables,
} from '@/client/services/hasura/generated/graphql'
import type { NotificationType } from '@/client/services/worlds/notifications/types'

import { notificationDetailsFromFragment } from '../../transforms/detailsFromFragments'
import type { NotificationDetails } from '../../types'

const PAGE_SIZE = 20

export default function useRealtimeNotifications({
  limit,
  skip = false,
}: {
  limit?: number | null
  skip?: boolean
}): {
  loading: boolean
  error: ApolloError | null
  notifications: NotificationDetails<NotificationType>[] | null
  onLoadMore: () => void
  hasMore: boolean
} {
  const user = useUser()

  const [recentNotifications, setRecentNotifications] = useState<NotificationFragment[] | null>(
    null
  )
  const [newNotifications, setNewNotifications] = useState<NotificationFragment[]>([])
  const [recentNotificationsLimit, setRecentNotificationsLimit] = useState(PAGE_SIZE)
  const [hasMore, setHasMore] = useState(true)

  const now = useMemo(() => new Date(), [])

  const onLoadMore = useCallback(() => setRecentNotificationsLimit(lim => lim + PAGE_SIZE), [])

  const { loading, error: recentNotificationsError } = useSubscription<
    NotificationsSubscription,
    NotificationsSubscriptionVariables
  >(NOTIFICATIONS_SUBSCRIPTION, {
    skip,
    variables: {
      limit: limit == null ? recentNotificationsLimit : Math.min(limit, recentNotificationsLimit),
      where: {
        user_id: { _eq: user.id },
        notify_at: {
          _lte: now,
        },
      },
    },
    onData: data => {
      const notifications = data.data.data?.notification ?? []
      setRecentNotifications(notifications)
      setHasMore(notifications.length >= recentNotificationsLimit)
    },
  })

  const { error: newNotificationsError } = useSubscription<
    NotificationsSubscription,
    NotificationsSubscriptionVariables
  >(NOTIFICATIONS_SUBSCRIPTION, {
    skip,
    variables: {
      where: {
        user_id: { _eq: user.id },
        notify_at: {
          _gt: now,
          _lte: 'now()',
        },
      },
    },
    onData: data => {
      const notifications = data.data.data?.notification ?? []
      setNewNotifications(notifications)
    },
  })

  return {
    loading,
    error: recentNotificationsError ?? newNotificationsError ?? null,
    notifications:
      recentNotifications == null
        ? null
        : [...newNotifications, ...recentNotifications].map(notificationDetailsFromFragment),
    onLoadMore,
    hasMore,
  }
}
